import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

import { cn } from '../../lib/cn';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { IconButton } from '../IconButton';
import styles from './Modal.module.css';

export type ModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly footer?: ReactNode;
  readonly children?: ReactNode;
  readonly size?: 'sm' | 'md' | 'lg';
  /** Скрывает заголовок визуально, но оставляет его для скринридеров. */
  readonly hideTitle?: boolean;
};

/** Фокус-трап, `Esc`, портал (docs/06 §4). */
export function Modal({ open, onClose, title, footer, children, size = 'md', hideTitle = false }: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(dialogRef, open, onClose);

  if (!open) {
    return null;
  }

  return createPortal(
    <div className={styles['overlay']} onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(styles['dialog'], styles[size])}
      >
        <header className={styles['header']}>
          <h2 className={cn(styles['title'], hideTitle && 'u-visually-hidden')}>{title}</h2>
          <IconButton label="Закрыть" icon={<X size={18} />} onClick={onClose} />
        </header>
        <div className={styles['body']}>{children}</div>
        {footer ? <footer className={styles['footer']}>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
